import { LowerCasePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ControlContainer, FormsModule, NgModelGroup } from '@angular/forms';
import { OperatorFilterEnum } from '@shared/enum/operator-filter.enum';
import { TypeFilterEnum } from '@shared/enum/type-filter.enum';
import { DateInput } from '@shared/models/filters/input-filter-date.class';
import { FiltersService, ResetFilter } from '@shared/services/filters.service';
import { Subscription } from 'rxjs';
import { InputLayoutComponent } from '../input-layout/input-layout.component';
import { TranslateModule } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'va-input-date',
  templateUrl: './input-date.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
  standalone: true,
  imports: [InputLayoutComponent, DatePickerModule, FormsModule, LowerCasePipe, TranslateModule],
})
export class InputDateComponent implements OnInit, OnDestroy {
  private filterService = inject(FiltersService);

  @Input() filterItem: DateInput;

  value: any[] | any;
  operator: OperatorFilterEnum | OperatorFilterEnum[];
  type: TypeFilterEnum;
  subscription: Subscription;
  composite: string[] = [null, null];

  ngOnInit(): void {
    this.type = this.filterItem.typeFilter;
    this.operator = this.type === 'Range' ? this.filterItem.operatorRange : this.filterItem.operatorSingle;

    this.getResetFilters();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  operatorChange(event: any): void {
    this.operator = event;
  }

  typeChange(event: any): void {
    this.type = event;
    this.operator = this.type === 'Range' ? this.filterItem.operatorRange : this.filterItem.operatorSingle;
  }

  setHourValue(e: any): void {
    if (!e) {
      this.value = null;
      return;
    }

    if (Array.isArray(e)) {
      this.value = e.map((date) => {
        if (!date) return null;
        if (date instanceof Date) return date;
        try {
          return this.parseDate(date);
        } catch {
          return null;
        }
      });
    } else {
      this.value = e instanceof Date ? e : this.parseDate(e);
    }
  }

  private parseDate(dateValue: any): Date | null {
    if (!dateValue) return null;

    // Handle ISO string format
    if (typeof dateValue === 'string') {
      return new Date(dateValue);
    }

    // Handle date object
    if (dateValue instanceof Date) {
      return new Date(dateValue.toISOString());
    }

    if (typeof dateValue === 'number') {
      return new Date(new Date(dateValue).toISOString());
    }

    // Handle formatted string (DD/MM/YYYY HH:mm)
    if (typeof dateValue === 'string' && dateValue.includes(' ')) {
      const [datePart, timePart] = dateValue.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hours, minutes] = timePart.split(':');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
    }

    return new Date(dateValue);
  }

  resetInput(): void {
    this.operator =
      this.filterItem.typeFilter === 'Range' ? this.filterItem.operatorRange : this.filterItem.operatorSingle;
    this.value = null;
  }

  restoreInput(field: any): void {
    if (typeof field.value === 'object' && Array.isArray(field.value)) {
      // range dates
      this.value = field.value.map((dateValue) => this.parseDate(dateValue));
    } else {
      // single date
      this.value = this.parseDate(field?.value);
    }
  }

  private getResetFilters(): void {
    this.subscription = this.filterService.getResetFilters().subscribe((state: ResetFilter) => {
      switch (state.type) {
        case 'restore':
          break;
        case 'resetAll':
          this.checkState(state.reset);
          break;
        case 'resetSingle':
          if (state.id === this.filterItem.key) {
            this.checkState(state.reset);
          }
          break;
      }
    });
  }

  private checkState(flag: boolean): void {
    if (flag) {
      this.value = null;
    }
  }
}
